import { extractCanonicalAiActions, type CanonicalAiAction } from '../utils/aiActionVerbs'
import type { AiModelId, AiTierId, ClarificationMetadata } from '../types/ai'
import { getTierPeakModel } from '../config/aiModelRegistry'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'

export type AiRouteTier = 'INSTANT' | 'STRUCTURAL'
export type AiModelName = AiModelId

/**
 * Semantic call classes for tier-aware model selection.
 * See docs/pricing/line-tier-plan.md §3 for the routing table.
 */
export type AiCallClass =
  | 'classifier'
  | 'interview_turn'
  | 'helper_chat'
  | 'small_edit'
  | 'large_refinement'
  | 'structural_draft'
  | 'attachment_ingest'
  | 'report'

export interface AiCallShape {
  class: AiCallClass
  /** Only relevant for structural_draft — 'small' stays on Sonnet even in Max. */
  complexity?: 'small' | 'large'
}

/** Call classes that escalate to the tier's peak model on Pro/Max. */
const HEAVY_CALL_CLASSES: ReadonlySet<AiCallClass> = new Set([
  'structural_draft',
  'large_refinement',
  'attachment_ingest',
  'report',
])

/**
 * Tier-aware model selector. The tier is a *quality ceiling*, not a lock:
 * cheap classes always route to Line's model regardless of tier. Only heavy
 * structural work + reports escalate to the tier's PEAK model (resolved from
 * `aiModelRegistry.json` → tierAssignments, with env override support).
 *
 * Zero-touch default: passing `tier === 'line'` (or omitted) always resolves
 * to whatever the registry names as Line's model — today `gemini-2.5-flash`.
 *
 * See docs/pricing/line-tier-plan.md §3 for the routing table and
 * docs/pricing/line-tier-integration-plan.md Phase J for the registry design.
 */
export const selectModel = (tier: AiTierId, call: AiCallShape): AiModelId => {
  const linePeak = getTierPeakModel('line')

  const chosen: AiModelId = (() => {
    // Cheap call classes: always Line's model, regardless of tier.
    if (
      call.class === 'classifier' ||
      call.class === 'interview_turn' ||
      call.class === 'helper_chat' ||
      call.class === 'small_edit'
    ) {
      return linePeak
    }
    // Line tier: no escalation.
    if (tier === 'line') return linePeak
    // Pro tier: escalate the heavy classes to the Pro peak.
    if (tier === 'line_pro') {
      return HEAVY_CALL_CLASSES.has(call.class) ? getTierPeakModel('line_pro') : linePeak
    }
    // Max tier: structural-large + report → Max peak; other heavy → Pro peak; cheap → Line.
    if (call.class === 'report') return getTierPeakModel('line_max')
    if (call.class === 'structural_draft' && call.complexity === 'large') return getTierPeakModel('line_max')
    if (HEAVY_CALL_CLASSES.has(call.class)) return getTierPeakModel('line_pro')
    return linePeak
  })()

  // === [AI-TIER-DEBUG] router pick — gated on ENABLE_ALL_FEATURES_FOR_TESTING.
  // See docs/pricing/line-tier-integration-plan.md Phase K for the removal
  // checklist to run before shipping. ===
  if (testingOverrideAllowsAllFeatures()) {
    console.log(
      `[AI-TIER-DEBUG] router.selectModel · tier=${tier} · callClass=${call.class}${call.complexity ? ` (${call.complexity})` : ''} → model=${chosen}`,
    )
  }
  // === /AI-TIER-DEBUG ===
  return chosen
}

export interface AiRouteRequest {
  prompt: string
  mode: 'generate' | 'edit'
  selectedTargetIds?: string[]
}

export interface AiRouteDecision {
  kind: 'ROUTE' | 'CLARIFY'
  tier: AiRouteTier
  model: AiModelName
  routerConfidence: number
  reasoning: string[]
  clarification?: string
  clarificationMetadata?: ClarificationMetadata
  actions: CanonicalAiAction[]
}

const STRUCTURAL_PATTERN = /\b(timeline|roadmap|project plan|parent[- ]?child|dependenc|prerequisite|graph|rebalance|redistribute|reorganize|restructure|multi[- ]?node|across (?:all|multiple))\b/i
const SIMPLE_PATTERN = /\b(checklist|workflow status|status|start date|end date|deadline|due date|toggle|complete|uncheck|shift|reschedule|postpone)\b/i
const EDIT_CONTEXT_PATTERN = /\b(checklist|workflow status|status|date|deadline|due|toggle|complete|shift|reschedule|edit|update|change|move)\b/i
const EXPLICIT_ID_PATTERN = /\b(?:goal|task|subtask)(?:_id)?\s*[:#-]?\s*[a-z0-9][\w-]*\b/i

const toClarificationMetadata = (
  clarification: string,
  actions: CanonicalAiAction[],
): ClarificationMetadata => {
  if (/\b(date|deadline|start|end|when)\b/i.test(clarification)) {
    return { field: 'date', inputType: 'DATE', label: clarification }
  }
  if (/\b(hour|cost|budget|amount|weight|rating|number)\b/i.test(clarification)) {
    return { field: 'value', inputType: 'NUMBER', label: clarification, min: 0, step: 1 }
  }
  if (actions.length > 1) {
    return {
      field: 'action', inputType: 'SELECT', label: clarification,
      options: actions.map((action) => action.replaceAll('_', ' ').toLowerCase()),
    }
  }
  if (/\b(goal|task|subtask|item|target|ID)\b/i.test(clarification)) {
    return { field: 'target', inputType: 'TEXT', label: clarification }
  }
  return { field: 'details', inputType: 'TEXT', label: clarification }
}

export const routeAiRequest = ({
  prompt,
  mode,
  selectedTargetIds = [],
}: AiRouteRequest): AiRouteDecision => {
  const normalized = prompt.trim()
  const actions = extractCanonicalAiActions(normalized)
  const structural = mode === 'generate' || STRUCTURAL_PATTERN.test(normalized)
  const tier: AiRouteTier = structural ? 'STRUCTURAL' : 'INSTANT'
  // Structural work previously routed to `gemini-2.5-pro`. Held at
  // `gemini-2.5-flash` until the vertexProxy Cloud Function is redeployed with
  // per-request `location` support (see aiModelRegistry.json tierAssignments
  // "$notes"). Revert this to `gemini-2.5-pro` once the proxy is live on the
  // updated code.
  const model: AiModelName = structural ? 'gemini-2.5-flash' : 'gemini-2.5-flash'

  if (actions.length > 2) {
    const clarification = 'Did you mean to perform these actions together? Please choose one or two changes for this request.'
    return {
      kind: 'CLARIFY', tier, model, routerConfidence: 0,
      reasoning: [`The request contains ${actions.length} distinct actions: ${actions.join(', ')}.`],
      clarification,
      clarificationMetadata: toClarificationMetadata(clarification, actions),
      actions,
    }
  }

  const editLike = mode === 'edit' || (!structural && EDIT_CONTEXT_PATTERN.test(normalized))
  const hasTarget = selectedTargetIds.length > 0 || EXPLICIT_ID_PATTERN.test(normalized)
  if (editLike && !hasTarget) {
    const clarification = 'Did you mean to change a specific goal or task? Select it or include its ID.'
    return {
      kind: 'CLARIFY', tier, model, routerConfidence: 0,
      reasoning: ['The request changes existing timeline data but does not identify a goal or task.'],
      clarification,
      clarificationMetadata: toClarificationMetadata(clarification, actions),
      actions,
    }
  }

  const exactSimple = !structural && SIMPLE_PATTERN.test(normalized) && hasTarget
  const routerConfidence = exactSimple ? 0.96 : structural ? 0.9 : 0.82
  return {
    kind: 'ROUTE', tier, model, routerConfidence,
    reasoning: [structural
      ? 'Structural generation or graph work requires the Pro reasoning tier.'
      : 'A scoped checklist, status, completion, or date operation can use the Flash tier.'],
    actions,
  }
}
